import { ComponentPropsWithoutRef, forwardRef, useId } from "react";
import styles from "./SideNav.module.scss";
import cx from "classnames";

/**
 * Props for the SideNavGroup component
 * @extends ComponentPropsWithoutRef<"li">
 */
export type SideNavGroupProps = ComponentPropsWithoutRef<"li"> & {
  /**
   * The label text displayed for the navigation group
   */
  label: string;
};

/**
 * SideNavGroup component for grouping related navigation links within a SideNav.
 *
 * Features:
 * - Groups related navigation links with labels
 * - Accessible with proper ARIA labeling
 * - Semantic grouping for screen readers
 * - Consistent styling and spacing
 * - Unique ID generation for accessibility
 *
 * @example
 * <SideNav.Group label="Getting Started">
 *   <SideNav.Link id="intro" href="/intro">
 *     Introduction
 *   </SideNav.Link>
 *   <SideNav.Link id="installation" href="/installation">
 *     Installation
 *   </SideNav.Link>
 * </SideNav.Group>
 */
export const SideNavGroup = forwardRef<HTMLLIElement, SideNavGroupProps>(
  (props, ref) => {
    const { children, label, ...rest } = props;

    const sideNavGroupClassNames = cx(styles["sidenav-group"]);

    const groupLabelId = useId();
    return (
      <li ref={ref} data-anv="sidenav-group" {...rest}>
        <label id={groupLabelId} className={styles["group-label"]}>
          {label}
        </label>
        <ul aria-labelledby={groupLabelId} className={sideNavGroupClassNames}>
          {children}
        </ul>
      </li>
    );
  },
);

SideNavGroup.displayName = "SideNavGroup";
